<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Pagination from '../../Pagination.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import InviteEmployerModal from './InviteEmployerModal.svelte';
  import Notification from '../../Notification.svelte';
  import { MyEmployerService, type Employer } from '../../../lib/api/employee/myEmployer.service';
  import { userStore } from '../../../stores/auth.store';

  // Modal state
  let showInviteModal = false;

  // State variables
  let loading = true;
  let error: string | null = null;
  let employers: Employer[] = [];
  let user: any = null;

  // Pagination
  let currentPage = 1;
  let paging: any = null;

  // Filter state
  let showArchivedOnly = false;
  let searchPhrase = '';
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  // Track if we've already loaded to prevent duplicate calls
  let hasLoaded = false;
  let isLoading = false;

  // Subscribe to user store
  userStore.subscribe(value => {
    user = value;
    // Only load if user becomes available and we haven't loaded yet
    if (user?.uuid && !hasLoaded && !isLoading) {
      loadEmployers();
    }
  });

  // Load employers on component mount
  onMount(async () => {
    if (user?.uuid && !hasLoaded && !isLoading) {
      await loadEmployers();
    }
  });

  async function loadEmployers() {
    if (!user?.uuid) {
      error = 'User not found';
      loading = false;
      return;
    }

    // Prevent duplicate calls
    if (isLoading) {
      return;
    }

    try {
      isLoading = true;
      loading = true;
      error = null;
      
      const result = await MyEmployerService.getEmployers(user.uuid, {
        page: currentPage,
        per_page: 10,
        only_archived: showArchivedOnly,
        search: searchPhrase || undefined
      });
      
      if (result.success) {
        // Use API status: rejected takes precedence, then pending (invited/ABN pending), else verified
        employers = (result.data || []).map(employer => ({
          ...employer,
          status: employer.status === 'rejected'
            ? 'rejected' as const
            : employer.status === 'unapproved' || employer.abn === 'PENDING'
              ? 'pending' as const
              : 'verified' as const
        }));
        paging = result.paging || null;
        hasLoaded = true;
      } else {
        error = result.message || 'Failed to load employers';
        employers = [];
        paging = null;
      }
    } catch (err: any) {
      error = err.message || 'An unexpected error occurred';
      employers = [];
      paging = null;
    } finally {
      loading = false;
      isLoading = false;
    }
  }

  // Use employers from API or fallback to empty array
  $: displayEmployers = employers.length > 0 ? employers : [];

  async function refreshEmployers() {
    hasLoaded = false; // Reset flag to allow refresh
    await loadEmployers();
  }

  function inviteEmployee() {
    showInviteModal = true;
  }

  function handleModalClose() {
    showInviteModal = false;
  }

  function handleEmployerLinked() {
    // Refresh the employer list after linking
    hasLoaded = false;
    loadEmployers();
  }

  function getStatusInfo(status: string) {
    if (status === 'rejected') {
      return { text: 'Rejected', class: 'status-rejected' };
    }
    if (status === 'verified') {
      return { text: 'Verified', class: 'status-verified' };
    }
    return { text: 'Pending', class: 'status-pending' };
  }

  function handlePageChange(event: CustomEvent) {
    const page = event.detail.page;
    if (page !== currentPage) {
      currentPage = page;
      loadEmployers();
    }
  }

  function toggleArchivedFilter() {
    showArchivedOnly = !showArchivedOnly;
    currentPage = 1; // Reset to first page when changing filter
    loadEmployers();
  }

  function handleSearch() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      currentPage = 1;
      hasLoaded = false;
      loadEmployers();
    }, 300);
  }
  </script>
  
<div class="employee-list-container">
  <div class="filters-section">
    <div class="filters-left">
      <SearchInput
        placeholder="Search employers by name..."
        bind:value={searchPhrase}
        on:search={handleSearch}
        on:input={handleSearch}
        width="250px"
      />
    </div>
    <div class="filters-right">
      <Button
        variant={showArchivedOnly ? "secondary" : "text"}
        on:click={toggleArchivedFilter}
      >
        <div class="button-content">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
            <path d="m3.3 7 8.7 5 8.7-5"/>
            <path d="M12 22V12"/>
          </svg>
          <span>{showArchivedOnly ? 'Show Active' : 'Show Archived'}</span>
        </div>
      </Button>

      <Button variant="primary" on:click={inviteEmployee}>
        <div class="button-content">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Invite Employer</span>
        </div>
      </Button>
    </div>
  </div>

  <div class="content">
    {#if loading}
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading employers...</p>
      </div>
    {:else if error}
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h3>Unable to Load Employers</h3>
        <p>{error}</p>
        <Button variant="primary" on:click={refreshEmployers}>
          Try Again
        </Button>
      </div>
    {:else if displayEmployers.length === 0}
      <div class="empty-container">
        <div class="empty-icon">🏢</div>
        <h3>No Employers Found</h3>
        <p>{searchPhrase ? 'Try adjusting your search terms.' : "You haven't been associated with any employers yet. Invite your employer to get started!"}</p>
        {#if searchPhrase}
          <Button variant="secondary" on:click={() => { searchPhrase = ''; hasLoaded = false; loadEmployers(); }}>
            Clear Search
          </Button>
        {/if}
        <Button variant="primary" on:click={inviteEmployee}>
          Invite Employer
        </Button>
      </div>
    {:else}
      <!-- Employers Grid -->
      <div class="employees-grid">
        {#each displayEmployers as employer (employer.uuid)}
        <div 
          class="employee-card"
          on:click={() => push(`/view-employer/${employer.uuid}`)}
          role="button"
          tabindex="0"
          on:keydown={(e) => e.key === 'Enter' && push(`/view-employer/${employer.uuid}`)}
        >
          <div class="employer-header">
            <div class="employer-info">
              <h3 class="employer-name">{employer.name}</h3>
              {#if employer.abn}
                <span class="employer-abn">ABN: {employer.abn}</span>
              {/if}
            </div>
            <div class="status-badge {getStatusInfo(employer.status || 'verified').class}">
              {#if (employer.status || 'verified') === 'rejected'}
                <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
              {:else if (employer.status || 'verified') === 'pending'}
                <svg class="status-icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              {:else}
                <svg class="status-icon" viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              {/if}
              {getStatusInfo(employer.status || 'verified').text}
            </div>
          </div>

          <div class="employer-details">
            {#if employer.abn}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="7" y1="8" x2="17" y2="8"></line>
                  <line x1="7" y1="12" x2="17" y2="12"></line>
                  <line x1="7" y1="16" x2="12" y2="16"></line>
                </svg>
                <span class="detail-text">ABN: {employer.abn}</span>
              </div>
            {/if}

            {#if employer.contact_email}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span class="detail-text">{employer.contact_email}</span>
              </div>
            {/if}

            {#if employer.head_office_location}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span class="detail-text">{employer.head_office_location}</span>
              </div>
            {/if}
          </div>

          <div class="employer-footer">
            <div class="view-link">
              View Details
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </div>
          </div>
        </div>
        {/each}
      </div>
  
      <!-- Pagination -->
      {#if paging && paging.last_page > 1}
        <div class="pagination-container">
          <Pagination
            {currentPage}
            totalPages={paging.last_page}
            maxVisiblePages={7}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<!-- Invite Employer Modal -->
<InviteEmployerModal 
  bind:isOpen={showInviteModal}
  existingEmployers={employers.map(e => ({ uuid: e.uuid, name: e.name }))}
  on:close={handleModalClose}
  on:employer-linked={handleEmployerLinked}
/>

<!-- Notification Component -->
<Notification
  show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  onDismiss={() => { notificationShow = false; }}
/>
  
  <style>
  .employee-list-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 100vh;
    background-color: #f7fafc;
    padding: 1.5rem;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
  }

  .filters-right {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-shrink: 0;
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
  }

  .status-badge::before {
    content: none;
    width: 0;
    height: 0;
    border-radius: 0;
  }

  .content {
    width: 100%;
  }

  /* Loading State */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-left-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Error State */
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    background: white;
    border-radius: 12px;
    padding: 3rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .error-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .error-container h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .error-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
  }

  /* Empty State */
  .empty-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    background: white;
    border-radius: 12px;
    padding: 3rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .empty-container h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .empty-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    }
  
    .employees-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }
  
  .employee-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .employee-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: var(--color-primary, #f0b429);
  }

  .employee-card:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .employer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  .employer-info {
    flex: 1;
    min-width: 0;
  }

  .employer-name {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
  }

  .employer-abn {
    font-size: 0.75rem;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    display: inline-block;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background: #d1fae5;
    color: #065f46;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .status-verified {
    background: #d1fae5;
    color: #065f46;
  }

  .status-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .status-icon {
    flex-shrink: 0;
  }

  .employer-details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .detail-text {
    color: #374151;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .employer-footer {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #f3f4f6;
  }

  .view-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--color-primary, #3b82f6);
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s;
  }

  .employee-card:hover .view-link {
    color: var(--color-primary-hover, #2563eb);
  }

  .view-link svg {
    transition: transform 0.2s;
  }

  .employee-card:hover .view-link svg {
    transform: translate(2px, -2px);
  }

    /* Responsive Design */
    @media (max-width: 768px) {
    .employee-list-container {
      padding: 1rem;
    }

    .filters-section {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .filters-right {
      width: 100%;
      flex-direction: column;
    }

    .filters-right :global(button) {
      width: 100%;
    }
  
      .employees-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
  }

  .button-icon {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .button-icon svg {
    width: 100%;
    height: 100%;
  }

  .button-text {
    line-height: 1.2;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }
  </style> 
